import React from 'react';

import { useMinWidth, useSurface } from '../../hooks/useSurface';

/**
 * The game-phase 3-zone grid.
 *
 *   [ --rail-left-w ] [ minmax(440px, 560px) ] [ --aside-w ]
 *
 * gap 32, centered in `--container-max`.
 *
 * Below 1024 the left and right zones are not rendered AT ALL (not hidden
 * — not rendered), so the phone tree is byte-identical to what it was
 * before this component existed. That is the whole contract.
 *
 * At 1024–1279 the LEFT zone drops first: on a 13" laptop the stage and
 * the table are what the extra width is *for*; the timer numeral and the
 * letters-so-far fold back into the top bar, where the phone keeps them.
 */
export interface StageLayoutProps {
  /** Timer numeral, letters-so-far. Dropped below 1280. */
  left?: React.ReactNode;
  /** THE stage. The only zone that exists on a phone. */
  children: React.ReactNode;
  /** The table — roster, totals. Dropped below 1024. */
  right?: React.ReactNode;
  /**
   * How much room the centre zone gets.
   *
   *   'stage'  440–560  the race. Four input fields; wider is not better.
   *   'wide'   440–720  the tally. The word is the star and it wants the room.
   *
   * A size knob on the primitive, NOT a per-screen `if (desktop)` — if a
   * screen ever needs a third measure, it goes here, not into the screen.
   */
  main?: 'stage' | 'wide';
  className?: string;
}

export function StageLayout({ left, children, right, main = 'stage', className = '' }: StageLayoutProps) {
  const surface = useSurface();
  // 1024–1279 — the left rail is the first thing to go.
  const wide = useMinWidth(1280);

  // Phone + tablet: the stage IS the screen. No grid, no wrapper, nothing
  // to regress.
  if (surface !== 'desktop') {
    return <>{children}</>;
  }

  const showLeft = !!left && wide;

  return (
    <div
      className={[
        'wr-stage',
        showLeft ? '' : 'no-left',
        main === 'wide' ? 'main-wide' : '',
        className,
      ].filter(Boolean).join(' ')}
    >
      {showLeft && <aside className="stage-left">{left}</aside>}
      <div className="stage-main">{children}</div>
      {right && <aside className="stage-right">{right}</aside>}
    </div>
  );
}
